import React, { useState, useEffect } from 'react';
import { AccountCircleIcon, CloseIcon, EmailIcon, HelpIcon, PhoneIcon } from './feather-icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './rag-chat-shell.css';

interface ContactInfo {
    name?: string;
    email?: string;
    phone?: string;
}

interface ContactInfoDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * ContactInfoDialog - Displays superadmin contact information from server
 * Fetches contact info from /api/v1/auth/contact-info endpoint
 * Only shows fields that are defined in server's .env file
 */
const ContactInfoDialog: React.FC<ContactInfoDialogProps> = ({
    open,
    onClose
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [contactInfo, setContactInfo] = useState<ContactInfo>({});
    const [hasContactInfo, setHasContactInfo] = useState(false);
    const [error, setError] = useState('');

    // Fetch contact info when dialog opens
    useEffect(() => {
        if (open) {
            fetchContactInfo();
        }
    }, [open]);

    const fetchContactInfo = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/api/v1/auth/contact-info');
            if (response.data.success) {
                setContactInfo(response.data.data || {});
                setHasContactInfo(response.data.hasContactInfo || false);
            } else {
                setError(t('contactInfo.fetchError', 'Failed to load contact information'));
            }
        } catch (err: any) {
            console.error('Error fetching contact info:', err);
            setError(t('contactInfo.fetchError', 'Failed to load contact information'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    if (!open) {
        return null;
    }

    return (
        <div className="industrial-dialog-backdrop" role="presentation" onMouseDown={handleClose}>
            <div
                className="industrial-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-info-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="industrial-dialog-header">
                    <h2 id="contact-info-title">
                        <HelpIcon fontSize="small" />
                        <span>{t('contactInfo.title', 'Contact Information')}</span>
                    </h2>
                    <button
                        type="button"
                        className="industrial-dialog-close"
                        onClick={handleClose}
                        aria-label={t('close', 'Close')}
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>
                <div className="industrial-dialog-body">
                    {loading ? (
                        <div className="industrial-loading-state">
                            {t('loading', { defaultValue: 'Loading...' })}
                        </div>
                    ) : error ? (
                        <div className="industrial-alert industrial-alert--error">{error}</div>
                    ) : !hasContactInfo ? (
                        <div className="industrial-alert industrial-alert--info">
                            {t('contactInfo.noInfo', 'No contact information is currently available.')}
                        </div>
                    ) : (
                        <div className="industrial-contact-list">
                            <p className="industrial-dialog-copy">
                                {t('contactInfo.description', 'If you need assistance, please reach out to us using the contact information below.')}
                            </p>

                            {/* Contact Name */}
                            {contactInfo.name && (
                                <div className="industrial-contact-row">
                                    <AccountCircleIcon fontSize="small" />
                                    <div>
                                        <span>{t('contactInfo.name', 'Name')}</span>
                                        <strong>{contactInfo.name}</strong>
                                    </div>
                                </div>
                            )}

                            {/* Contact Email */}
                            {contactInfo.email && (
                                <div className="industrial-contact-row">
                                    <EmailIcon fontSize="small" />
                                    <div>
                                        <span>{t('contactInfo.email', 'Email')}</span>
                                        <strong>
                                            <a href={`mailto:${contactInfo.email}`}>
                                                {contactInfo.email}
                                            </a>
                                        </strong>
                                    </div>
                                </div>
                            )}

                            {/* Contact Phone */}
                            {contactInfo.phone && (
                                <div className="industrial-contact-row">
                                    <PhoneIcon fontSize="small" />
                                    <div>
                                        <span>{t('contactInfo.phone', 'Phone')}</span>
                                        <strong>
                                            <a href={`tel:${contactInfo.phone}`}>
                                                {contactInfo.phone}
                                            </a>
                                        </strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="industrial-dialog-footer">
                    <button type="button" className="industrial-button" onClick={handleClose}>
                        {t('close', 'Close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactInfoDialog;
